export type AddressFormType = {
  line1: string
  line2: string
  city: string
  state: string
  postalCode: string
}

export type AddressFormErrorType = {
  line1: boolean
  city: boolean
  state: boolean
  postalCode: boolean
}

export interface FhirAddress {
  city?: string
  country?: string
  id?: string
  line?: string[]
  postalCode?: string
  state?: string
  text?: string
  type?: string
  use?: string
}

export interface Coding {
  code?: string
  display?: string
  id?: string
  system?: string
  userSelected?: boolean
  version?: string
}

export interface CodeableConcept {
  coding?: Coding[]
  id?: string
  text?: string
}

export interface Identifier {
  assigner?: Reference
  id?: string
  system?: string
  type?: CodeableConcept
  use?: string
  value?: string
}

export interface ResourceObject {
  id?: string
  implicitRules?: string
  language?: string
  resourceType?: string
}

export const isResourceObject = (resource?: unknown): resource is ResourceObject =>
  typeof resource === "object" &&
  resource !== null &&
  "resourceType" in resource &&
  typeof resource.resourceType === "string"

export interface Reference {
  display?: string
  id?: string
  identifier?: Identifier
  localRef?: string
  resource?: unknown[] | boolean | number | null | ResourceObject | string
  resourceType?: string
  type?: string
  uri?: string
}

export interface Parameters {
  id?: string
  implicitRules?: string
  language?: string
  parameter?: ParametersParameterArrayArray[]
  resourceType?: "Parameters"
}

export interface ParametersParameterArrayArray {
  id?: string
  name: string
  part?: ParametersParameterArrayArray[]
  resource?: unknown[] | boolean | number | null | ResourceObject | string
  value?: ParametersParameterArrayValue
}

export interface ParametersParameterArrayValue {
  Address?: FhirAddress
  base64Binary?: string
  boolean?: boolean
  canonical?: string
  code?: string
  CodeableConcept?: CodeableConcept
  Coding?: Coding
  date?: string
  dateTime?: string
  decimal?: number
  id?: string
  Identifier?: Identifier
  instant?: Date
  integer?: number
  markdown?: string
  oid?: string
  positiveInt?: number
  Range?: Range
  Reference?: Reference
  string?: string
  time?: string
  unsignedInt?: number
  uri?: string
  url?: string
  uuid?: string
}

export type CustomError = Error & { cause: { name: string; message: string; issues?: string[]; traceId?: string } }

export interface Extension {
    extension?: Extension[]
    id?: string
    modifierExtension?: Extension[]
    url?: string
    valueAddress?: FhirAddress
    valueAge?: unknown
    valueAnnotation?: unknown
    valueAttachment?: unknown
    valueBase64Binary?: string
    valueBoolean?: boolean
    valueCanonical?: string
    valueCode?: string
    valueCodeableConcept?: CodeableConcept
    valueCoding?: Coding
    valueContactDetail?: unknown
    valueContactPoint?: unknown
    valueContributor?: unknown
    valueCount?: unknown
    valueDataRequirement?: unknown
    valueDate?: string
    valueDateTime?: string
    valueDecimal?: number
    valueDistance?: unknown
    valueDosage?: unknown
    valueDuration?: unknown
    valueElementDefinition?: unknown
    valueEmail?: string
    valueExpression?: unknown
    valueHumanName?: unknown
    valueId?: string
    valueIdentifier?: Identifier
    valueInstant?: Date
    valueInteger?: number
    valueKeyword?: string
    valueMap?: unknown
    valueMarkdown?: string
    valueMarketingStatus?: unknown
    valueMeta?: unknown
    valueMoney?: unknown
    valueMoneyQuantity?: unknown
    valueNarrative?: Narrative
    valueNumber?: number
    valueOid?: string
    valueParameterDefinition?: unknown
    valuePassword?: string
    valuePeriod?: unknown
    valuePopulation?: unknown
    valuePositiveInt?: number
    valueProdCharacteristic?: unknown
    valueProductShelfLife?: unknown
    valueQuantity?: unknown
    valueRange?: unknown
    valueRatio?: unknown
    valueReference?: Reference
    valueRelatedArtifact?: unknown
    valueSampledData?: unknown
    valueSearchParameterExpression?: unknown
    valueSignature?: unknown
    valueSimpleQuantity?: unknown
    valueString?: string
    valueSubstanceAmount?: unknown
    valueTime?: string
    valueTiming?: unknown
    valueTriggerDefinition?: unknown
    valueUnsignedInt?: number
    valueUri?: string
    valueUrl?: string
    valueUsageContext?: unknown
    valueUuid?: string
    valueXhtml?: string
    type?: string
}

export interface OperationOutcomeIssueArray {
    code: string
    details?: CodeableConcept
    diagnostics?: string
    expression?: string[]
    extension?: Extension[]
    id?: string
    location?: string[]
    modifierExtension?: Extension[]
    severity: string
}
export interface Narrative {
    div: string
    extension?: Extension[]
    id?: string
    modifierExtension?: Extension[]
    status: string
}

export interface OperationOutcome {
    contained?: Array<unknown[] | boolean | number | null | ResourceObject | string>
    extension?: Extension[]
    id?: string
    implicitRules?: string
    issue: OperationOutcomeIssueArray[]
    language?: string
    modifierExtension?: Extension[]
    resourceType?: "OperationOutcome"
    text?: Narrative
}

export const isOperationOutcome = (resource?: unknown): resource is OperationOutcome =>
    isResourceObject(resource) && resource.resourceType === "OperationOutcome"
